import moment from '../../lib/moment-fi'
import type {
  FeedbackContent,
  FeedbackTarget,
  FeedbackType,
  NewFeedback,
} from '../../types/Feedback'
import type { RestDay, Shift } from '../../types/Input'

export type DraftAction =
  | { type: 'toggleEmphasis'; value: string }
  | { type: 'toggleError'; value: string }
  | { type: 'setMessage'; value: string }
  | { type: 'setErrorMessage'; value: string }
  | { type: 'toggleRequestAnswer' }
  | { type: 'setStars'; value: number }

export const isBadRating = (stars: number): boolean => stars < 3

const toggle = <T>(list: T[], value: T): T[] =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value]

export const initialDraft = (stars: number): FeedbackContent => ({
  stars,
  emphasis: [],
  errors: [],
  message: '',
  errorMessage: '',
  requestAnswer: false,
})

export const draftReducer = (draft: FeedbackContent, action: DraftAction): FeedbackContent => {
  switch (action.type) {
    case 'toggleEmphasis':
      return { ...draft, emphasis: toggle(draft.emphasis, action.value) }
    case 'toggleError':
      return { ...draft, errors: toggle(draft.errors, action.value) }
    case 'setMessage':
      return { ...draft, message: action.value }
    case 'setErrorMessage':
      return { ...draft, errorMessage: action.value }
    case 'toggleRequestAnswer':
      return { ...draft, requestAnswer: !draft.requestAnswer }
    case 'setStars':
      // Good and bad ratings ask different questions, so answers don't carry over
      return isBadRating(draft.stars) === isBadRating(action.value)
        ? { ...draft, stars: action.value }
        : { ...draft, stars: action.value, emphasis: [], errors: [], errorMessage: '' }
  }
}

export const toNewFeedback = (draft: FeedbackContent, target: FeedbackTarget): NewFeedback => {
  const bad = isBadRating(draft.stars)
  return {
    ...target,
    ...draft,
    errors: bad ? draft.errors : [],
    errorMessage: bad ? draft.errorMessage : '',
  }
}

export const toFeedbackTarget = (
  scope: FeedbackType,
  shift: Shift | RestDay | null
): FeedbackTarget | null => {
  if (!shift) return null
  if (scope === 'shift') {
    return 'id' in shift
      ? {
          type: 'shift',
          targetId: shift.id,
          shiftNumber: shift.shiftId,
          shiftDate: moment(shift.startDateTime).format('YYYY-MM-DD'),
        }
      : null
  }
  return shift.listId ? { type: 'schedule', targetId: shift.listId } : null
}
